import React, { useEffect, useMemo, useRef } from 'react';
import clsx from 'clsx';
import IconStar from 'utils/icon/icon-star.svg';
import { formatCartLinesAjax } from 'utils/storefront/format-resource';

const availableTiers = window.TMW.discountTiers;

const CartIncentive = ({ cart, cta }) => {
  const progressBar = useRef(null);
  const lines = cart?.mode === 'storefront' ? cart?.state?.lines : formatCartLinesAjax(cart?.state?.items);

  const addLinkToMessage = (text) => {
    if (!text) return text;
    const regex = /\[(.*?)\]/g;
    const replacedText = text.replace(regex, (match, contents) => {
      return cta ? `<a href="${cta}">${contents}</a>` : contents;
    });

    return replacedText;
  };

  const qualifyingProducts = useMemo(() => {
    return lines.reduce((acc, line) => {
      const lineProduct = cart.productsData[line.product_id];
      if (!lineProduct) return acc;
      return acc + (lineProduct.tags.includes('custom-set-eligible') ? line.quantity : 0);
    }, 0);
  }, [lines, cart.productsData]);

  const message = useMemo(() => {
    if (qualifyingProducts >= availableTiers[availableTiers.length - 1]?.minProducts) {
      return window.TMW.Strings.cart.discounts.maxDiscount.replace(
        '{discount}',
        availableTiers[availableTiers.length - 1].discountPercentage,
      );
    }
    if (qualifyingProducts === 0) {
      return window.TMW.Strings.cart.discounts.noProducts.replace('{discount}', availableTiers[0].discountPercentage);
    }

    for (const tier of availableTiers) {
      if (qualifyingProducts >= tier.minProducts && qualifyingProducts <= tier.maxProducts) {
        const nextTier = availableTiers.find((t) => t.minProducts > tier.maxProducts);
        if (nextTier) {
          return window.TMW.Strings.cart.discounts[
            nextTier.minProducts - qualifyingProducts > 1 ? 'multiple' : 'single'
          ].replace('{discount}', nextTier.discountPercentage);
        }
      }
    }

    const firstTier = availableTiers[0];
    if (qualifyingProducts < firstTier.minProducts) {
      return window.TMW.Strings.cart.discounts[
        firstTier.minProducts - qualifyingProducts > 1 ? 'multiple' : 'single'
      ].replace('{discount}', firstTier.discountPercentage);
    }

    return null;
  }, [qualifyingProducts]);

  useEffect(() => {
    if (qualifyingProducts >= availableTiers[availableTiers.length - 1]?.minProducts) {
      progressBar.current?.style.setProperty('width', '100%');
    } else if (qualifyingProducts === 0) {
      progressBar.current?.style.setProperty('width', '0%');
    } else {
      const percentage = (qualifyingProducts / (availableTiers[availableTiers.length - 1]?.minProducts || 0)) * 100;
      progressBar.current?.style.setProperty('width', `calc(${percentage}%)`);
    }
  }, [qualifyingProducts]);

  return (
    <div className="color-scheme-6 px-3 py-3 lg:px-8">
      <div
        className="body-3 [&>a]:body-3-underline mb-2 text-center"
        dangerouslySetInnerHTML={{ __html: addLinkToMessage(message) }}
      ></div>
      <div className="relative w-full">
        <div className="relative z-10 flex items-center justify-between">
          <IncentiveIcon
            unlocked={qualifyingProducts >= 1}
            text={window.TMW.Strings.cart.discounts.firstPair}
            iconClass="text-left items-start"
          />
          {availableTiers.map((item, index) => {
            const unlocked = qualifyingProducts >= availableTiers[index]?.minProducts;
            return (
              <IncentiveIcon
                key={item.discountPercentage}
                item={item}
                unlocked={unlocked}
                text={window.TMW.Strings.cart.discounts.percentageOff.replace(
                  '{discount}',
                  availableTiers[index].discountPercentage,
                )}
                iconClass={index === availableTiers.length - 1 ? `text-right items-end` : `text-center items-center`}
              />
            );
          })}
        </div>
        <div className="absolute top-0 flex h-7.5 w-full items-center justify-center">
          <div className="relative h-1 w-full overflow-hidden bg-black">
            <div
              className={clsx('absolute top-0 h-full bg-brand-tangerine-dark', {
                '-left-10': qualifyingProducts === 2,
                '-left-[3.125rem]': qualifyingProducts === 1,
                '-left-7.5': qualifyingProducts > 2,
                '-left-5': qualifyingProducts === (availableTiers[availableTiers.length - 1]?.minProducts || 0) - 1,
              })}
              ref={progressBar}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartIncentive;

const IncentiveIcon = ({ unlocked, text, iconClass }) => {
  return (
    <div className={clsx('relative flex w-[3.125rem] flex-col', iconClass)}>
      <div
        className={clsx('flex size-7.5 items-center justify-center rounded-full border-2 [&>svg]:w-2.5', {
          'border-brand-tangerine-dark bg-brand-tangerine-dark text-white': unlocked,
          'border-black bg-white text-grey-medium': !unlocked,
        })}
      >
        <IconStar />
      </div>
      <span
        className={clsx('body-3 mt-1.5', {
          'text-default': unlocked,
          'text-grey-dark': !unlocked,
        })}
      >
        {text}
      </span>
    </div>
  );
};
